import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Icon, type IconType } from "~/components/ui/icons/icon";

export interface MenuAction {
  icon: IconType;
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: "destructive";
  disabled?: boolean;
  subActions?: MenuAction[];
}

type MaybeAction = MenuAction | false | null | undefined;

interface ActionsMenuProps {
  actions: MaybeAction[][];
  label?: string;
  align?: "start" | "end";
  className?: string;
}

export function ActionsMenu({ actions, label = "More actions", align = "end", className }: ActionsMenuProps) {
  const groups = actions.map((group) => group.filter(Boolean) as MenuAction[]).filter((group) => group.length > 0);
  if (groups.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-slot="actions-menu"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          title={label}
          className={className}
        >
          <Icon i="more-vertical" className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {groups.map((group, gi) => (
          <Fragment key={gi}>
            {gi > 0 && <DropdownMenuSeparator />}
            {group.map((action) =>
              action.subActions ? (
                <SubMenu key={action.label} action={action} />
              ) : (
                <ActionItem key={action.label} action={action} />
              ),
            )}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ActionItem({ action }: { action: MenuAction }) {
  if (action.href) {
    return (
      <DropdownMenuItem asChild variant={action.variant} disabled={action.disabled}>
        <Link to={action.href}>
          <Icon i={action.icon} className="text-muted-foreground" />
          {action.label}
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem onClick={action.onClick} variant={action.variant} disabled={action.disabled}>
      <Icon i={action.icon} className="text-muted-foreground" />
      {action.label}
    </DropdownMenuItem>
  );
}

function SubMenu({ action }: { action: MenuAction }) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Icon i={action.icon} className="text-muted-foreground" />
        {action.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
        {action.subActions!.map((sub) => (
          <ActionItem key={sub.label} action={sub} />
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
